//path: src\types\assetTypes.ts
import { SupabaseClipType } from './clipTypes';
import { Clip } from './clipTypes';

export interface SupabaseAssetType {
  user_id: string | undefined;
  asset_type_id: string;
  asset_type_source_name: string;
  asset_type_display_name: string;
  asset_type_img_url: string;
  asset_type_source_url?: string;
  asset_type_category: string | null;
  asset_type_details?: Record<string, unknown>;
  asset_type_tags?: string[];
  asset_type_clips?: SupabaseClipType[];
  asset_type_position?: string;
  asset_type_rotation?: string;
  asset_type_scale?: string;
  asset_type_begin_time?: number;
  asset_type_end_time?: number;
  asset_type_is_active: boolean;
  asset_type_display_on: boolean;
  asset_type_status?: 'uploaded' | 'processed';
  asset_type_altitude?: number | null;
  asset_type_latitude?: number | null;
  asset_type_longitude?: number | null;
  /** True for stacked-alpha MP4 video assets (top half RGB, bottom half alpha mask). Set by the backend converter. */
  asset_type_has_packed_alpha?: boolean;
}

export interface SupabaseAssetInstance {
  id?: number;
  user_id: string;
  asset_type_id: string;
  scene_instance_id: string;
  asset_instance_id: string;
  asset_instance_source_name: string;
  asset_instance_display_name: string;
  asset_instance_img_url: string;
  asset_instance_category: string | null;
  asset_instance_details?: Record<string, unknown>;
  asset_instance_tags?: string[];
  asset_instance_clips?: SupabaseClipType[];
  asset_instance_is_locked?: boolean;
  asset_instance_is_selected?: boolean;
  asset_instance_is_active?: boolean;
  asset_instance_position: string;
  asset_instance_rotation: string;
  asset_instance_scale: string;
  asset_instance_source_url?: string;
  asset_instance_begin_time: number;
  asset_instance_end_time: number;
  asset_instance_point_cloud_url?: string;
  asset_instance_metadata_url?: string;
  asset_instance_altitude?: number | null;
  asset_instance_longitude?: number | null;
  asset_instance_latitude?: number | null;
  clip_instance_id?: string;
  asset_instance_display_order?: number | null;
  /** Mirrors AssetType.has_packed_alpha at instance creation. True for stacked-alpha MP4 video assets. */
  asset_instance_has_packed_alpha?: boolean;
  asset_instance_is_billboarded?: boolean;
}

export interface Asset {
  typeId: string;
  userId: string | undefined;
  typeSourceName: string;
  typeDisplayName: string;
  typeImgUrl: string;
  type: string | null;
  typeDetails?: Record<string, unknown>;
  typeTags?: string[];
  typeClips?: Clip[];
  lastSelectedClip?: string;
  typeSource: string;
  typePosition: string;
  typeRotation: string;
  typeScale: string;
  createdAt?: string;
  typeAltitude?: number | null;
  typeLatitude?: number | null;
  typeLongitude?: number | null;
}

export interface AssetInstance {
  userId: string;
  typeId: string;
  sceneInstanceId: string;
  instanceId: string;
  instanceSourceName: string;
  instanceDisplayName: string;
  instanceImgUrl: string;
  instanceCategory: string | null;
  instanceDetails?: Record<string, unknown>;
  instanceTags?: string[];
  instanceClips?: Clip[];
  isLocked?: boolean;
  isVisible?: boolean;
  isSelected?: boolean;
  isSplitPending?: boolean;
  lastSelectedClip?: string;
  instancePosition: string;
  instanceRotation: string;
  instanceScale: string;
  instanceSrcUrl?: string;
  isLoading: boolean;
  instanceBeginTime: number;
  instanceEndTime: number;
  isActive?: boolean;
  isMuted?: boolean;
  instanceAltitude?: number | null;
  instanceLongitude?: number | null;
  instanceLatitude?: number | null;
  instanceDisplayOrder?: number | null;
  isBillboarded?: boolean;
}

export interface ITimelineAsset {
  duration?: number;
  startTime: number;
  endTime?: number;
  imageSrc?: string;
  action?: string;
  assetInstance?: AssetInstance;
  audioUrl?: string;
}

export interface AssetTypes {
  characterAssetTypes: Asset[];
  propAssetTypes: Asset[];
  maskAssetTypes: Asset[];
  isLoading: boolean;
  error?: string;
}

// CAMERA_FEATURE: Camera creation and timeline display is temporarily disabled.
// Set to true to re-enable. See: SceneAdapter, TimelineInit, useTimelineInitialization.
export const ENABLE_CAMERA = false;

export const ASSET_CATEGORIES = {
  CAMERA: 'camera',
  TAP_TO_PLACE: 'tap_to_place',
  GEOSPATIAL: 'geospatial_anchor',
  AUDIO: 'audio',
  IMMERSAL: 'immersal',
  IMAGE: 'image',
  IMAGE_ANCHOR: 'image_anchor',
  VIDEO: 'video',
  WALK: 'clipAsset',
  SCREEN_UI: 'screen_ui',
  TRIGGER: 'trigger',
  OPEN_BRUSH: 'open_brush'
} as const;

/** Anchor types: only these can be swapped when the selected asset is an anchor. */
const ANCHOR_CATEGORIES = [ASSET_CATEGORIES.IMMERSAL, ASSET_CATEGORIES.IMAGE_ANCHOR, ASSET_CATEGORIES.GEOSPATIAL] as const;
export function isAnchorCategory(category: string | null | undefined): boolean {
  return !!category && ANCHOR_CATEGORIES.includes(category as typeof ANCHOR_CATEGORIES[number]);
}

export const ASSET_SOURCES = {
  CAMERA: 'camera_guy',
  TAP_TO_PLACE: 'system_tap_to_place',
  GEOSPATIAL: 'system_geospatial',
  AUDIO: 'system_audio',
  IMMERSAL: 'system_immersal_anchor',
  IMAGE: 'Image',
  VIDEO: 'system_video',
  WALK: 'system_locomotion'
} as const;